"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { EllipsisVerticalIcon, PlusIcon } from "@heroicons/react/24/outline";
import { CompactMenu, type CompactMenuItem } from "@/components/compact-menu";
import { TabToggle } from "@/components/common/TabToggle";
import { useSearchParams } from "next/navigation";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import { useWaveCurations } from "@/hooks/waves/useWaveCurations";
import { useWave } from "@/hooks/useWave";
import { useDecisionPoints } from "@/hooks/waves/useDecisionPoints";
import { useWaveTimers } from "@/hooks/useWaveTimers";
import { Time } from "@/helpers/time";
import { useAuth } from "@/components/auth/Auth";
import { MyStreamWaveTab } from "@/types/waves.types";
import { useContentTab, WaveVotingState, type SetActiveContentTab } from "../ContentTabContext";
import MyStreamWaveCurationCreateDialog from "./tabs/MyStreamWaveCurationCreateDialog";
import MyStreamActionTooltip from "./MyStreamActionTooltip";
interface MyStreamWaveDesktopTabsProps {
  readonly activeTab: MyStreamWaveTab;
  readonly wave: ApiWave;
  readonly setActiveTab: SetActiveContentTab;
  readonly activeCurationId: string | null;
  readonly onSelectCuration: (curationId: string | null) => void;
}
interface TabOption {
  readonly key: string;
  readonly label: string;
  readonly panelId: string;
}
const getContentTabPanelId = (tab: MyStreamWaveTab): string => `my-stream-wave-tabpanel-${tab.toLowerCase()}`;
const getCurationPanelId = (curationId: string): string => `my-stream-wave-tabpanel-curation-${curationId}`;
const AUTO_EXPAND_LIMIT = 5;
const MOBILE_INLINE_CURATION_LIMIT = 1;
const TAB_LABELS: Record<MyStreamWaveTab, string> = {
  [MyStreamWaveTab.CHAT]: "Chat",
  [MyStreamWaveTab.LEADERBOARD]: "Leaderboard",
  [MyStreamWaveTab.SALES]: "Sales",
  [MyStreamWaveTab.WINNERS]: "Winners",
  [MyStreamWaveTab.OUTCOME]: "Outcome",
  [MyStreamWaveTab.MY_VOTES]: "My Votes",
  [MyStreamWaveTab.FAQ]: "FAQ"
};
const getWaveVotingState = ({
  isUpcoming,
  isCompleted
}: {
  readonly isUpcoming: boolean;
  readonly isCompleted: boolean;
}): WaveVotingState => {
  if (isUpcoming) {
    return WaveVotingState.NOT_STARTED;
  }
  if (isCompleted) {
    return WaveVotingState.ENDED;
  }
  return WaveVotingState.ONGOING;
};
const MyStreamWaveDesktopTabs: React.FC<MyStreamWaveDesktopTabsProps> = t0 => {
  const $ = _c(122);
  const {
    activeTab,
    wave,
    setActiveTab,
    activeCurationId,
    onSelectCuration
  } = t0;
  const searchParams = useSearchParams();
  const {
    availableTabs,
    updateAvailableTabs,
    setActiveContentTab
  } = useContentTab();
  const {
    connectedProfile
  } = useAuth();
  const hasAuthenticatedProfile = Boolean(connectedProfile?.handle);
  const {
    isChatWave,
    isMemesWave,
    isCurationWave,
    pauses: t1
  } = useWave(wave);
  const {
    filterDecisionsDuringPauses
  } = t1;
  const {
    voting: t2,
    decisions: t3
  } = useWaveTimers(wave);
  const {
    isUpcoming,
    isCompleted
  } = t2;
  const {
    firstDecisionDone
  } = t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      initialPastWindow: 3,
      initialFutureWindow: 10
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const {
    allDecisions,
    hasMoreFuture,
    loadMoreFuture
  } = useDecisionPoints(wave, t4);
  let t5;
  if ($[1] !== wave.id) {
    t5 = {
      waveId: wave.id
    };
    $[1] = wave.id;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const {
    data: t6
  } = useWaveCurations(t5);
  let t7;
  if ($[3] !== t6) {
    t7 = t6 === undefined ? [] : t6;
    $[3] = t6;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const curations = t7;
  const canManageCurations = wave.wave.authenticated_user_eligible_for_admin === true;
  let t8;
  if ($[5] !== allDecisions || $[6] !== filterDecisionsDuringPauses) {
    const decisionsAsApiFormat = allDecisions.map(_temp);
    const filtered = filterDecisionsDuringPauses(decisionsAsApiFormat);
    t8 = allDecisions.filter(decision_0 => filtered.some(item => item.decision_time === decision_0.timestamp));
    $[5] = allDecisions;
    $[6] = filterDecisionsDuringPauses;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const filteredDecisions = t8;
  let t9;
  if ($[8] !== filteredDecisions) {
    t9 = filteredDecisions.find(_temp2)?.timestamp ?? null;
    $[8] = filteredDecisions;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const nextDecisionTime = t9;
  const autoExpandFutureAttemptsRef = useRef(0);
  const desktopTabsScrollerRef = useRef(null);
  const mobileTabsScrollerRef = useRef(null);
  const [isCreateCurationOpen, setIsCreateCurationOpen] = useState(false);
  let t10;
  let t11;
  if ($[10] !== hasMoreFuture || $[11] !== loadMoreFuture || $[12] !== nextDecisionTime) {
    t10 = () => {
      const hasUpcoming = typeof nextDecisionTime === "number";
      if (hasUpcoming || !hasMoreFuture) {
        autoExpandFutureAttemptsRef.current = 0;
        return;
      }
      if (autoExpandFutureAttemptsRef.current >= AUTO_EXPAND_LIMIT) {
        return;
      }
      const timeoutId = globalThis.setTimeout(() => {
        autoExpandFutureAttemptsRef.current = autoExpandFutureAttemptsRef.current + 1;
        loadMoreFuture();
      }, 50);
      return () => {
        clearTimeout(timeoutId);
      };
    };
    t11 = [nextDecisionTime, hasMoreFuture, loadMoreFuture];
    $[10] = hasMoreFuture;
    $[11] = loadMoreFuture;
    $[12] = nextDecisionTime;
    $[13] = t10;
    $[14] = t11;
  } else {
    t10 = $[13];
    t11 = $[14];
  }
  useEffect(t10, t11);
  let t12;
  if ($[15] !== isCompleted || $[16] !== isUpcoming) {
    t12 = getWaveVotingState({
      isUpcoming,
      isCompleted
    });
    $[15] = isCompleted;
    $[16] = isUpcoming;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const votingState = t12;
  let t13;
  if ($[18] !== firstDecisionDone || $[19] !== hasAuthenticatedProfile || $[20] !== isChatWave || $[21] !== isCurationWave || $[22] !== isMemesWave || $[23] !== searchParams || $[24] !== updateAvailableTabs || $[25] !== votingState || $[26] !== wave.id) {
    t13 = () => {
      const hasSerialTarget = searchParams.get("serialNo") !== null;
      updateAvailableTabs({
        waveId: wave.id,
        isMemesWave,
        isChatWave,
        hasAuthenticatedProfile,
        isCurationWave,
        votingState,
        hasFirstDecisionPassed: firstDecisionDone,
        transientPreferredTab: hasSerialTarget ? MyStreamWaveTab.CHAT : null
      });
    };
    $[18] = firstDecisionDone;
    $[19] = hasAuthenticatedProfile;
    $[20] = isChatWave;
    $[21] = isCurationWave;
    $[22] = isMemesWave;
    $[23] = searchParams;
    $[24] = updateAvailableTabs;
    $[25] = votingState;
    $[26] = wave.id;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== firstDecisionDone || $[29] !== hasAuthenticatedProfile || $[30] !== isChatWave || $[31] !== isCurationWave || $[32] !== isMemesWave || $[33] !== searchParams || $[34] !== updateAvailableTabs || $[35] !== votingState || $[36] !== wave) {
    t14 = [wave, isMemesWave, isChatWave, hasAuthenticatedProfile, isCurationWave, votingState, firstDecisionDone, searchParams, updateAvailableTabs];
    $[28] = firstDecisionDone;
    $[29] = hasAuthenticatedProfile;
    $[30] = isChatWave;
    $[31] = isCurationWave;
    $[32] = isMemesWave;
    $[33] = searchParams;
    $[34] = updateAvailableTabs;
    $[35] = votingState;
    $[36] = wave;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[38] !== activeCurationId || $[39] !== setActiveContentTab || $[40] !== wave.wave.type) {
    t15 = () => {
      if (wave.wave.type === ApiWaveType.Chat && !activeCurationId) {
        setActiveContentTab(MyStreamWaveTab.CHAT);
      }
    };
    t16 = [wave.wave.type, activeCurationId, setActiveContentTab];
    $[38] = activeCurationId;
    $[39] = setActiveContentTab;
    $[40] = wave.wave.type;
    $[41] = t15;
    $[42] = t16;
  } else {
    t15 = $[41];
    t16 = $[42];
  }
  useEffect(t15, t16);
  let t17;
  if ($[43] !== availableTabs || $[44] !== hasAuthenticatedProfile || $[45] !== isCurationWave || $[46] !== isMemesWave) {
    let t18;
    if ($[48] !== hasAuthenticatedProfile || $[49] !== isCurationWave || $[50] !== isMemesWave) {
      t18 = tab => {
        if (tab === MyStreamWaveTab.MY_VOTES) {
          return isCurationWave || isMemesWave && hasAuthenticatedProfile;
        }
        if (tab === MyStreamWaveTab.SALES) {
          return isCurationWave;
        }
        if (tab === MyStreamWaveTab.FAQ) {
          return isMemesWave;
        }
        return true;
      };
      $[48] = hasAuthenticatedProfile;
      $[49] = isCurationWave;
      $[50] = isMemesWave;
      $[51] = t18;
    } else {
      t18 = $[51];
    }
    t17 = availableTabs.filter(t18).map(_temp3);
    $[43] = availableTabs;
    $[44] = hasAuthenticatedProfile;
    $[45] = isCurationWave;
    $[46] = isMemesWave;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  const standardOptions = t17;
  let t18;
  if ($[52] !== curations) {
    t18 = curations.map(_temp4);
    $[52] = curations;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  const curationOptions = t18;
  let t19;
  if ($[54] !== curationOptions || $[55] !== standardOptions) {
    t19 = [...standardOptions, ...curationOptions];
    $[54] = curationOptions;
    $[55] = standardOptions;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  const options = t19;
  const activeKey = activeCurationId ? `curation:${activeCurationId}` : activeTab;
  let t20;
  bb0: {
    if (curationOptions.length <= MOBILE_INLINE_CURATION_LIMIT) {
      t20 = curationOptions;
      break bb0;
    }
    let t21;
    if ($[57] !== activeKey || $[58] !== curationOptions) {
      t21 = curationOptions.find(option => option.key === activeKey) ?? null;
      $[57] = activeKey;
      $[58] = curationOptions;
      $[59] = t21;
    } else {
      t21 = $[59];
    }
    const activeCurationOption = t21;
    let visibleOptions;
    if ($[60] !== activeCurationOption || $[61] !== curationOptions) {
      visibleOptions = activeCurationOption ? [activeCurationOption] : [];
      for (const option_0 of curationOptions) {
        if (visibleOptions.length >= MOBILE_INLINE_CURATION_LIMIT) {
          break;
        }
        if (option_0.key === activeCurationOption?.key) {
          continue;
        }
        visibleOptions.push(option_0);
      }
      $[60] = activeCurationOption;
      $[61] = curationOptions;
      $[62] = visibleOptions;
    } else {
      visibleOptions = $[62];
    }
    t20 = visibleOptions;
  }
  const mobileVisibleCurationOptions = t20;
  let t21;
  if ($[63] !== mobileVisibleCurationOptions) {
    t21 = new Set(mobileVisibleCurationOptions.map(_temp5));
    $[63] = mobileVisibleCurationOptions;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  const visibleKeys = t21;
  let t22;
  if ($[65] !== curationOptions || $[66] !== visibleKeys) {
    let t23;
    if ($[68] !== visibleKeys) {
      t23 = option_2 => !visibleKeys.has(option_2.key);
      $[68] = visibleKeys;
      $[69] = t23;
    } else {
      t23 = $[69];
    }
    t22 = curationOptions.filter(t23);
    $[65] = curationOptions;
    $[66] = visibleKeys;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  const mobileOverflowCurationOptions = t22;
  let t23;
  if ($[70] !== mobileVisibleCurationOptions || $[71] !== standardOptions) {
    t23 = [...standardOptions, ...mobileVisibleCurationOptions];
    $[70] = mobileVisibleCurationOptions;
    $[71] = standardOptions;
    $[72] = t23;
  } else {
    t23 = $[72];
  }
  const mobileOptions = t23;
  let t24;
  if ($[73] !== mobileOverflowCurationOptions || $[74] !== onSelectCuration) {
    let t25;
    if ($[76] !== onSelectCuration) {
      t25 = option_3 => ({
        id: option_3.key,
        label: option_3.label,
        onSelect: () => onSelectCuration(option_3.key.replace("curation:", ""))
      });
      $[76] = onSelectCuration;
      $[77] = t25;
    } else {
      t25 = $[77];
    }
    t24 = mobileOverflowCurationOptions.map(t25);
    $[73] = mobileOverflowCurationOptions;
    $[74] = onSelectCuration;
    $[75] = t24;
  } else {
    t24 = $[75];
  }
  const mobileOverflowItems = t24;
  const createCurationTooltipId = `my-stream-create-curation-${wave.id}`;
  const showCreateFirstCurationCallout = canManageCurations && curations.length === 0;
  let t25;
  if ($[78] !== createCurationTooltipId || $[79] !== showCreateFirstCurationCallout) {
    t25 = showCreateFirstCurationCallout ? {} : {
      "data-tooltip-id": createCurationTooltipId,
      "data-tooltip-content": "Create curation"
    };
    $[78] = createCurationTooltipId;
    $[79] = showCreateFirstCurationCallout;
    $[80] = t25;
  } else {
    t25 = $[80];
  }
  const createButtonTooltipProps = t25;
  let t26;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = () => {
      const frameId = globalThis.window.requestAnimationFrame(() => {
        [desktopTabsScrollerRef.current, mobileTabsScrollerRef.current].forEach(_temp6);
      });
      return () => {
        globalThis.window.cancelAnimationFrame(frameId);
      };
    };
    $[81] = t26;
  } else {
    t26 = $[81];
  }
  let t27;
  if ($[82] !== activeKey || $[83] !== options) {
    t27 = [activeKey, options];
    $[82] = activeKey;
    $[83] = options;
    $[84] = t27;
  } else {
    t27 = $[84];
  }
  useEffect(t26, t27);
  if (isChatWave) {
    return null;
  }
  let t28;
  if ($[85] !== onSelectCuration || $[86] !== setActiveTab) {
    t28 = key => {
      if (key.startsWith("curation:")) {
        onSelectCuration(key.replace("curation:", ""));
        return;
      }
      onSelectCuration(null);
      setActiveTab(key as MyStreamWaveTab);
    };
    $[85] = onSelectCuration;
    $[86] = setActiveTab;
    $[87] = t28;
  } else {
    t28 = $[87];
  }
  let t29;
  if ($[88] !== activeKey || $[89] !== mobileOptions || $[90] !== t28) {
    t29 = <TabToggle options={mobileOptions} activeKey={activeKey} onSelect={t28} />;
    $[88] = activeKey;
    $[89] = mobileOptions;
    $[90] = t28;
    $[91] = t29;
  } else {
    t29 = $[91];
  }
  let t30;
  if ($[92] !== mobileOverflowItems) {
    t30 = mobileOverflowItems.length > 0 && <CompactMenu triggerClassName="tw-inline-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white" trigger={<EllipsisVerticalIcon className="tw-h-5 tw-w-5" />} aria-label="More curations" items={mobileOverflowItems} menuWidthClassName="tw-w-52" />;
    $[92] = mobileOverflowItems;
    $[93] = t30;
  } else {
    t30 = $[93];
  }
  let t31;
  if ($[94] !== t29 || $[95] !== t30) {
    t31 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-1 sm:tw-hidden"><div ref={mobileTabsScrollerRef} className="tw-min-w-0 tw-flex-1 tw-overflow-x-auto tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300"><div className="tw-inline-flex tw-items-center tw-gap-1">{t29}{t30}</div></div></div>;
    $[94] = t29;
    $[95] = t30;
    $[96] = t31;
  } else {
    t31 = $[96];
  }
  let t32;
  if ($[97] !== onSelectCuration || $[98] !== setActiveTab) {
    t32 = key_0 => {
      if (key_0.startsWith("curation:")) {
        onSelectCuration(key_0.replace("curation:", ""));
        return;
      }
      onSelectCuration(null);
      setActiveTab(key_0 as MyStreamWaveTab);
    };
    $[97] = onSelectCuration;
    $[98] = setActiveTab;
    $[99] = t32;
  } else {
    t32 = $[99];
  }
  let t33;
  if ($[100] !== activeKey || $[101] !== options || $[102] !== t32) {
    t33 = <div ref={desktopTabsScrollerRef} className="tw-hidden tw-min-w-0 tw-flex-1 tw-overflow-x-auto tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300 sm:tw-block"><TabToggle options={options} activeKey={activeKey} onSelect={t32} /></div>;
    $[100] = activeKey;
    $[101] = options;
    $[102] = t32;
    $[103] = t33;
  } else {
    t33 = $[103];
  }
  let t34;
  if ($[104] !== canManageCurations || $[105] !== createButtonTooltipProps || $[106] !== showCreateFirstCurationCallout) {
    t34 = canManageCurations && <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2"><button type="button" onClick={() => setIsCreateCurationOpen(true)} {...createButtonTooltipProps} className={showCreateFirstCurationCallout ? "tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-3.5 tw-py-2 tw-text-xs tw-font-semibold tw-text-iron-100 tw-transition desktop-hover:hover:tw-border-iron-500 desktop-hover:hover:tw-bg-iron-800 desktop-hover:hover:tw-text-white" : "tw-inline-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition desktop-hover:hover:tw-border-iron-500 desktop-hover:hover:tw-bg-iron-800 desktop-hover:hover:tw-text-white"} aria-label="Create curation"><PlusIcon className={`tw-h-4 tw-w-4 tw-flex-shrink-0 ${showCreateFirstCurationCallout ? "-tw-ml-1" : ""}`} />{showCreateFirstCurationCallout && <span>Create first curation</span>}</button></div>;
    $[104] = canManageCurations;
    $[105] = createButtonTooltipProps;
    $[106] = showCreateFirstCurationCallout;
    $[107] = t34;
  } else {
    t34 = $[107];
  }
  let t35;
  if ($[108] !== t31 || $[109] !== t33 || $[110] !== t34) {
    t35 = <div className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-px-2 tw-@container/tabs sm:tw-px-4">{t31}{t33}{t34}</div>;
    $[108] = t31;
    $[109] = t33;
    $[110] = t34;
    $[111] = t35;
  } else {
    t35 = $[111];
  }
  let t36;
  if ($[112] !== createCurationTooltipId) {
    t36 = <MyStreamActionTooltip id={createCurationTooltipId} />;
    $[112] = createCurationTooltipId;
    $[113] = t36;
  } else {
    t36 = $[113];
  }
  let t37;
  if ($[114] !== isCreateCurationOpen || $[115] !== onSelectCuration || $[116] !== wave) {
    t37 = isCreateCurationOpen && <MyStreamWaveCurationCreateDialog wave={wave} isOpen={isCreateCurationOpen} onClose={() => setIsCreateCurationOpen(false)} onSaved={curation_0 => onSelectCuration(curation_0.id)} />;
    $[114] = isCreateCurationOpen;
    $[115] = onSelectCuration;
    $[116] = wave;
    $[117] = t37;
  } else {
    t37 = $[117];
  }
  let t38;
  if ($[118] !== t35 || $[119] !== t36 || $[120] !== t37) {
    t38 = <>{t35}{t36}{t37}</>;
    $[118] = t35;
    $[119] = t36;
    $[120] = t37;
    $[121] = t38;
  } else {
    t38 = $[121];
  }
  return t38;
};
export default MyStreamWaveDesktopTabs;
function _temp(decision) {
  return {
    decision_time: decision.timestamp
  };
}
function _temp2(decision_1) {
  return decision_1.timestamp > Time.currentMillis();
}
function _temp3(tab_0) {
  return {
    key: tab_0,
    label: TAB_LABELS[tab_0],
    panelId: getContentTabPanelId(tab_0)
  };
}
function _temp4(curation) {
  return {
    key: `curation:${curation.id}`,
    label: curation.name,
    panelId: getCurationPanelId(curation.id)
  };
}
function _temp5(option_1) {
  return option_1.key;
}
function _temp6(scroller) {
  if (!scroller) {
    return;
  }
  const activeTabElement = scroller.querySelector("[role=\"tab\"][aria-selected=\"true\"]");
  activeTabElement?.scrollIntoView({
    block: "nearest",
    inline: "nearest"
  });
}